/**
 * Port of the mobile `AutoSplitCalculationHelper`
 * (`lib/feature/paid_by/calculation/auto_split_calculation_helper.dart`) — the split / paid-by
 * DISTRIBUTION + rounding-remainder rules, kept faithful so web and mobile split a bill identically.
 * All amounts are MINOR units (integers).
 *
 * Every mode computes each ENABLED participant's raw share, then makes the enabled shares sum EXACTLY
 * to `total`:
 *   • Equal — spread the leftover ±1 minor unit at a time across the enabled participants from the
 *     LAST backward, stepping by the difference's sign (so a negative total / refund works too);
 *   • Amount / Shares / Percentage — the LAST eligible participant absorbs the whole difference.
 * Disabled participants (and, for Shares/Percentage, those with a zero weight) get 0 and never absorb.
 */
export type SplitTypeWire = 'Equal' | 'Amount' | 'Shares' | 'Percentage';

/** One participant fed to the calculator (mirrors mobile `SplitAmountModel`'s inputs). */
export interface AutoSplitUser {
  readonly id: string;
  readonly splitEnabled: boolean;
  /** 'Amount' mode — this participant's entered share in MAJOR currency units (e.g. `23.45`). */
  readonly amount?: number;
  /** 'Shares' mode — this participant's share weight (e.g. `2`). */
  readonly share?: number;
  /** 'Percentage' mode — this participant's percentage (e.g. `33.33`). */
  readonly percentage?: number;
}

/** Per-participant calculated amount (MINOR units), keyed by participant id. */
type Amounts = Record<string, number>;

const zeros = (users: readonly AutoSplitUser[]): Amounts => {
  const out: Amounts = {};
  users.forEach((u) => {
    out[u.id] = 0;
  });
  return out;
};

/** Equal split — mobile `shareByEqual`. */
function shareByEqual(total: number, users: readonly AutoSplitUser[]): Amounts {
  const enabledCount = users.filter((u) => u.splitEnabled).length;
  if (enabledCount === 0) return zeros(users);
  // Integer division toward zero (Dart `~/`) so a negative total truncates, not floors.
  const splitAmount = Math.trunc(total / enabledCount);
  const out: Amounts = {};
  users.forEach((u) => {
    out[u.id] = u.splitEnabled ? splitAmount : 0;
  });
  let diff = total - enabledCount * splitAmount;
  if (diff !== 0) {
    const step = diff < 0 ? -1 : 1;
    for (let i = users.length - 1; i >= 0 && diff !== 0; i -= 1) {
      const u = users[i];
      if (u?.splitEnabled) {
        out[u.id] = (out[u.id] ?? 0) + step;
        diff -= step;
      }
    }
  }
  return out;
}

/** Absorb the whole `diff` on the LAST participant matching `eligible` (mobile's break-on-last rule). */
function absorbOnLast(
  out: Amounts,
  users: readonly AutoSplitUser[],
  diff: number,
  eligible: (u: AutoSplitUser) => boolean,
): void {
  if (diff === 0) return;
  for (let i = users.length - 1; i >= 0; i -= 1) {
    const u = users[i];
    if (u && eligible(u)) {
      out[u.id] = (out[u.id] ?? 0) + diff;
      break;
    }
  }
}

/** By exact amount — mobile `shareByAmount`. `amount` is MAJOR units → ×100 to minor. */
function shareByAmount(total: number, users: readonly AutoSplitUser[]): Amounts {
  const out: Amounts = {};
  users.forEach((u) => {
    out[u.id] = u.splitEnabled ? Math.round((u.amount ?? 0) * 100) : 0;
  });
  const sumEnabled = users.reduce((acc, u) => acc + (u.splitEnabled ? (out[u.id] ?? 0) : 0), 0);
  absorbOnLast(out, users, total - sumEnabled, (u) => u.splitEnabled);
  return out;
}

/** By shares — mobile `splitByShares`. Only enabled participants with a positive share count. */
function splitByShares(total: number, users: readonly AutoSplitUser[]): Amounts {
  const eligible = (u: AutoSplitUser): boolean => u.splitEnabled && (u.share ?? 0) > 0;
  const totalShares = users.filter(eligible).reduce((acc, u) => acc + (u.share ?? 0), 0);
  if (totalShares === 0) return zeros(users);
  const perShare = Math.trunc(total / totalShares);
  const out: Amounts = {};
  users.forEach((u) => {
    out[u.id] = eligible(u) ? Math.trunc(u.share ?? 0) * perShare : 0;
  });
  const sumEnabled = users.reduce((acc, u) => acc + (eligible(u) ? (out[u.id] ?? 0) : 0), 0);
  absorbOnLast(out, users, total - sumEnabled, eligible);
  return out;
}

/** By percentage — mobile `splitByPercentage`. Percentages kept to 2 dp via basis points. */
function splitByPercentage(total: number, users: readonly AutoSplitUser[]): Amounts {
  const eligible = (u: AutoSplitUser): boolean => u.splitEnabled && (u.percentage ?? 0) > 0;
  const totalPct = users.filter(eligible).reduce((acc, u) => acc + (u.percentage ?? 0), 0);
  if (totalPct === 0) return zeros(users);
  const out: Amounts = {};
  users.forEach((u) => {
    if (eligible(u)) {
      // 33.33 % → 3333 basis points; ÷10000 (not ÷100) preserves the two decimal places.
      const basisPoints = Math.round((u.percentage ?? 0) * 100);
      out[u.id] = Math.trunc((total * basisPoints) / 10000);
    } else {
      out[u.id] = 0;
    }
  });
  const sumEnabled = users.reduce((acc, u) => acc + (eligible(u) ? (out[u.id] ?? 0) : 0), 0);
  absorbOnLast(out, users, total - sumEnabled, eligible);
  return out;
}

/**
 * Distribute `totalMinor` across `users` by `splitType`, applying the mobile remainder rules so the
 * enabled participants' calculated amounts sum EXACTLY to the total. Returns a map of id → minor.
 */
export function calculateAutoSplit(
  totalMinor: number,
  splitType: SplitTypeWire,
  users: readonly AutoSplitUser[],
): Amounts {
  if (users.length === 0) return {};
  switch (splitType) {
    case 'Amount':
      return shareByAmount(totalMinor, users);
    case 'Shares':
      return splitByShares(totalMinor, users);
    case 'Percentage':
      return splitByPercentage(totalMinor, users);
    case 'Equal':
    default:
      return shareByEqual(totalMinor, users);
  }
}
